import cn from 'clsx';
import Link from 'next/link';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import useIsMobile from '@/src/hooks/useIsMobile';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, Text, UserPreview} from '../../ui';
import css from '../styles/ContentCreator.module.css';

const MAX_CONTENT_LENGTH = 100;

interface ContentCreatorProps {
  image: string;
  aboutContentCreator: string;
  rating: number;
  name: string;
  isFollowed: boolean;
  onToggleFollowClick: () => void;
  isLoadingFollow: boolean;
  slug: string;
  withFollowButton: boolean;
  isClinic: boolean;
}

const ContentCreator = ({
  image,
  aboutContentCreator,
  rating,
  name,
  isFollowed,
  onToggleFollowClick,
  isLoadingFollow,
  slug,
  withFollowButton,
  isClinic,
}: ContentCreatorProps) => {
  const {isMaxWidth600} = useIsMobile();
  const {USER_PROFILE, CLINIC_TARGET_PROFILE} = useRoutePaths();
  const [isOpen, setIsOpen] = useState(false);
  const [shortContentCreatorText, setShortContentCreatorText] = useState('');

  const toggleText = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const profileLinkNode = useMemo(
    () => (
      <Link
        href={
          isClinic
            ? CLINIC_TARGET_PROFILE.getLink(slug)
            : USER_PROFILE.getLink(slug)
        }
      >
        <a className={css.viewProfile} aria-label={'user link to profile'}>
          <Text
            color={Text.COLOR.GRADIENT}
            fontSize={Text.FONT_SIZE.SIZE_16}
            lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
          >
            <TextLocal id={'courseDetails.view-profile'} />
          </Text>
        </a>
      </Link>
    ),
    [slug, isClinic]
  );

  useEffect(() => {
    if (
      aboutContentCreator?.length &&
      aboutContentCreator.length > MAX_CONTENT_LENGTH
    ) {
      setShortContentCreatorText(
        `${aboutContentCreator?.slice(0, MAX_CONTENT_LENGTH)}...`
      );
    } else {
      setShortContentCreatorText(aboutContentCreator);
    }
  }, [aboutContentCreator]);

  return (
    <div
      className={cn(css.contentCreator, {
        [css.contentCreatorFullwidth]: !withFollowButton,
      })}
    >
      <div>
        <Link
          href={
            isClinic
              ? CLINIC_TARGET_PROFILE.getLink(slug)
              : USER_PROFILE.getLink(slug)
          }
        >
          <a>
            <UserPreview
              rating={rating}
              image={image}
              name={name}
              isMobileColumn
              size={isMaxWidth600 ? 'medium' : 'regular'}
            />
          </a>
        </Link>
        {isMaxWidth600 && withFollowButton && (
          <>
            <div className={css.buttonWrapper}>
              <Button
                height={Button.HEIGHT.REGULAR}
                color={isFollowed ? Button.COLOR.RED : Button.COLOR.PRIMARY}
                type={Button.TYPES.BUTTON}
                isLoading={isLoadingFollow}
                onClick={onToggleFollowClick}
              >
                <TextLocal
                  id={
                    isFollowed
                      ? 'courseDetails.unfollow'
                      : 'courseDetails.follow'
                  }
                />
              </Button>
            </div>
            {profileLinkNode}
          </>
        )}
        <div className={css.aboutWrapper}>
          <div>
            <Text
              fontSize={Text.FONT_SIZE.SIZE_14}
              color={Text.COLOR.GREY}
              lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: isOpen
                    ? aboutContentCreator
                    : shortContentCreatorText,
                }}
              />
            </Text>
          </div>
          {Boolean(
            aboutContentCreator?.length &&
              aboutContentCreator.length > MAX_CONTENT_LENGTH
          ) && (
            <button onClick={toggleText} className={css.showMoreButton}>
              <Text
                color={Text.COLOR.GRADIENT}
                fontSize={Text.FONT_SIZE.SIZE_16}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              >
                <TextLocal
                  id={
                    isOpen
                      ? 'courseDetails.show-less'
                      : 'courseDetails.show-more'
                  }
                />
              </Text>
            </button>
          )}
        </div>
      </div>
      {!isMaxWidth600 && withFollowButton && (
        <div className={css.buttonWrapper}>
          <Button
            height={Button.HEIGHT.REGULAR}
            color={isFollowed ? Button.COLOR.RED : Button.COLOR.PRIMARY}
            type={Button.TYPES.BUTTON}
            isLoading={isLoadingFollow}
            onClick={onToggleFollowClick}
          >
            <TextLocal
              id={
                isFollowed ? 'courseDetails.unfollow' : 'courseDetails.follow'
              }
            />
          </Button>
          {profileLinkNode}
        </div>
      )}
    </div>
  );
};

export default ContentCreator;
